// externals
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// utils
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import { changeVehicleCreators, changeVehicleSelectors, middlewareMgr, AI_VEHICLE_SELECTED } from '../../../store';
import { useValidateVehicle } from '../../../utils';
import { createVehicleSelectionEvent } from '../../../analytics';
import { useVehicleSearchFiltersContext } from '../../changeVehicle/VehicleSearchFiltersContext';
import { useAddNewScenarioAvailability } from '../../changeVehicle/useAddNewScenarioAvailability';

// types
import type { CatalogVehicleWithIndex, VehicleCustomData } from '../../../types';
import { dealerSelectors } from '@makemydeal/dr-shared-store';

type GetNormalizedRetailPrice = {
    retailPrice: string;
    listedPrice: string;
    isNewVehicle: boolean;
};

const getNormalizedRetailPrice = ({ retailPrice, listedPrice, isNewVehicle }: GetNormalizedRetailPrice) => {
    if (isNewVehicle) {
        return retailPrice ? parseFloat(retailPrice) : 0;
    }

    const listedPriceNumber = listedPrice === '' ? 0 : parseFloat(listedPrice);
    return retailPrice === '' ? listedPriceNumber : parseFloat(retailPrice);
};

export const useVehicleListRowItemInStock = (vehicle: CatalogVehicleWithIndex) => {
    const {
        year,
        make = '',
        model = '',
        trim = '',
        vin = '',
        stockNumber = '',
        listedPrice = '',
        mileage = '',
        condition = '',
        retailPrice = '',
        exteriorColor = '',
        interiorColor = '',
        driveLine = '',
        imageUrl = '',
        ageDays = null
    } = vehicle;

    const dispatch = useDispatch();
    const { appliedFilters } = useVehicleSearchFiltersContext();

    const isDealerAISearchEnabled = useSelector(dealerSelectors.isDealerAISearchEnabled);
    const isAgeOfInventorySearchEnabled = useSelector(changeVehicleSelectors.isAgeOfInventorySearchEnabled);
    const shouldShowAgeOfInventory = isDealerAISearchEnabled && isAgeOfInventorySearchEnabled;

    const vehicleWithCustomData = useSelector(
        isDealerAISearchEnabled
            ? changeVehicleSelectors.getAgentVehicleWithCustomData(vehicle.vin!)
            : changeVehicleSelectors.getVehicleWithCustomData(vehicle.vin!)
    );

    // Use custom data from Redux if available, otherwise fall back to vehicle prop
    const [customListedPrice, setCustomListedPrice] = useState(String(vehicleWithCustomData?.listedPrice ?? listedPrice));
    const [customMileage, setCustomMileage] = useState(String(vehicleWithCustomData?.mileage ?? mileage));
    const [customRetailPrice, setCustomRetailPrice] = useState(String(vehicleWithCustomData?.retailPrice ?? retailPrice));

    const selectedVehicle = useSelector(changeVehicleSelectors.getSelectedVehicle);
    const isChangeProcessing = useSelector(changeVehicleSelectors.isChangeProcessing);

    const isVehicleValid = useValidateVehicle(vehicle);

    // The buttons are judged on the very vehicle their click acts on — the selected one, carrying the dealer's edits —
    // so a button can never say one thing while the click does another. Reading this row's own vehicle instead would
    // drift apart from the action, because a row counts as selected by its position in the list and a new page or a
    // reworded search can put a different vehicle in that position while the selection stays put.
    // `isVehicleValid` above reads the untouched prop, so it never changes while the dealer types: it decides whether
    // the inputs appear and cannot drive the buttons. Indeterminate validity leaves the actions disabled.
    const selectedVehicleWithCustomData = useSelector(changeVehicleSelectors.getSelectedVehicleWithCustomData);
    const isInlineActionsDisabled = !useValidateVehicle(selectedVehicleWithCustomData);

    const isSelected = useMemo(() => selectedVehicle.cardIndex === vehicle.cardIndex, [selectedVehicle, vehicle]);

    const shouldShowInputs = useMemo(() => isSelected && !isVehicleValid, [isSelected, isVehicleValid]);
    const vehicleListItemTitle = `${condition} ${year} ${make} ${model} ${trim}`;
    const isNewVehicle = condition === 'new';
    const minOdometerValue = useMemo(() => (isNewVehicle ? 0 : 1), [isNewVehicle]);
    const formattedPrice = useMemo(
        () => formatDollarsAndCents(vehicleWithCustomData?.listedPrice, '—'),
        [isNewVehicle, vehicle, vehicleWithCustomData]
    );

    const agentPromptString = useSelector(changeVehicleSelectors.getAgentPromptString);

    // Shared by both the whole-row click and the inline Select Vehicle button — keeps the CDL/AI_VEHICLE_SELECTED/
    // newVehicleSelected dispatch sequence in one place rather than duplicating it per caller.
    const dispatchVehicleSelection = () => {
        /* istanbul ignore next */
        const resultsRank = (vehicle.cardIndex || 0) + 1;

        // Dispatch vs-003 with optional search query for AI mode
        const analyticsEvent = createVehicleSelectionEvent({
            vehicle,
            appliedFilters,
            resultsRank,
            searchQuery: agentPromptString || undefined,
            aiMode: isDealerAISearchEnabled
        });
        const hostAction = middlewareMgr.hostActions.forwardCDLEvent(analyticsEvent.payload);
        dispatch(hostAction);

        // Dispatch AI vehicle selected action for SV search context forwarding
        if (isDealerAISearchEnabled) {
            dispatch({
                type: AI_VEHICLE_SELECTED,
                payload: { vehicle, appliedFilters, resultsRank, searchTerm: agentPromptString }
            });
        }

        dispatch(changeVehicleCreators.newVehicleSelected(vehicle));
    };

    // Whole-row click (primaryAction): guarded so re-clicking an already-selected row (e.g. clicking into one of
    // its editable inputs) doesn't redundantly re-fire analytics/postMessage.
    const selectVehicle = () => {
        if (!isSelected) {
            dispatchVehicleSelection();
        }
    };

    // Inline Select Vehicle button: the row is already selected by the time the button shows, so all that is left is to
    // ask the footer to apply it. The footer stays the only place that applies, since only it knows whether this host
    // confirms first. Selecting again here would fire the selection analytics a second time.
    const handleSelectVehicleClick = () => {
        dispatch(changeVehicleCreators.setApplyVehicleRequested(true));
    };

    // The limit is shared with the footer so the two cannot disagree about it. Validity is the row's own, already
    // computed above from the edited data, so New Scenario and Select Vehicle gate on one reading rather than two.
    const { canAddNewScenario, isScenarioLimitReached } = useAddNewScenarioAvailability();
    const isAddNewScenarioDisabled = isInlineActionsDisabled || isScenarioLimitReached;

    // Creating the scenario ends with the dealer back on the dashboard, and only the footer knows whether that means
    // closing a slideout or navigating, so the row asks and the footer does it — the same split as the apply above.
    const handleAddNewScenarioClick = () => {
        dispatch(changeVehicleCreators.setAddNewScenarioRequested(true));
    };

    const updateVehicleCustomData = (value: VehicleCustomData) => {
        dispatch(changeVehicleCreators.updateVehicleCustomData(vin, value));
    };

    const getToUpdateNormalizedDataFields = () => ({
        listedPrice: customListedPrice ? parseFloat(customListedPrice) : 0,
        retailPrice: getNormalizedRetailPrice({
            retailPrice: customRetailPrice,
            listedPrice: customListedPrice,
            isNewVehicle
        }),
        mileage: customMileage ? Number(customMileage) : 0
    });

    const handleCustomMileageChange = (value: string) => {
        setCustomMileage(value);
        updateVehicleCustomData({
            ...getToUpdateNormalizedDataFields(),
            mileage: value ? Number(value) : undefined
        });
    };

    const handleCustomSellingPrice = (value: string) => {
        setCustomListedPrice(value);
        updateVehicleCustomData({
            ...getToUpdateNormalizedDataFields(),
            listedPrice: value ? parseFloat(value) : 0
        });
    };

    const handleCustomRetailPrice = (value: string) => {
        setCustomRetailPrice(value);
        updateVehicleCustomData({
            ...getToUpdateNormalizedDataFields(),
            retailPrice: getNormalizedRetailPrice({
                retailPrice: value,
                listedPrice: customListedPrice,
                isNewVehicle
            })
        });
    };

    return {
        // Vehicle data
        year,
        make,
        model,
        trim,
        vin,
        stockNumber,
        listedPrice,
        mileage,
        condition,
        retailPrice,
        exteriorColor,
        interiorColor,
        driveLine,
        imageUrl,
        ageDays,
        shouldShowAgeOfInventory,

        // Computed values
        vehicleListItemTitle,
        isNewVehicle,
        isSelected,
        shouldShowInputs,
        formattedPrice,
        minOdometerValue,
        isInlineActionsDisabled,
        canAddNewScenario,
        isAddNewScenarioDisabled,
        isChangeProcessing,

        // Custom state
        customListedPrice,
        customMileage,
        customRetailPrice,

        // Handlers
        selectVehicle,
        handleSelectVehicleClick,
        handleAddNewScenarioClick,
        handleCustomMileageChange,
        handleCustomSellingPrice,
        handleCustomRetailPrice
    };
};
